import { Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../../enums/misc.enum';
import { retrieveOnboardingResponseToTab } from '../../../helpers/merchant-onboarding';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import {
  IPaginationMeta,
  OnboardingInformationByTab,
} from '../../../type/admin.types';
import {
  RetrieveOneMerchantAccountsRequest,
  RetrieveOneMerchantAccountsResponse,
  RetrieveOneMerchantResponse,
} from '../../../type/entity.types';
import { ErrorResponse } from '../../../type/error-response.types';
import { BasicAccount } from '../../../type/retrieve-account.types';
import BackButton from '../../buttons/back-button.component';
import EntityInfo from './components/entity-info.component';

function MerchantInfo(props: Readonly<Props>) {
  const { setComponentManager, selectedEntity } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState<
    OnboardingInformationByTab | undefined
  >();
  const axiosPrivate = useAxiosPrivate();
  const [retrieveAccountsParams, setRetrieveAccountsParams] =
    useState<RetrieveOneMerchantAccountsRequest>({
      page: 1,
      merchantId: selectedEntity,
    });
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const [merchantData, setMerchantData] =
    useState<RetrieveOneMerchantResponse>();

  const [userData, setUserData] = useState<BasicAccount[]>();

  const loadAccountsData = async () => {
    try {
      setIsLoading(true);

      const response = await axiosPrivate.post(
        `/web/merchants/accounts`,
        retrieveAccountsParams
      );

      const data: RetrieveOneMerchantAccountsResponse = response.data;

      setUserData(data.items);
      setPaginationData(data.metadata);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-merchant-accounts',
        message:
          error?.error ||
          err?.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsAccountsLoading(false);
    }
  };

  const paginationRequest = (page: number) => {
    setRetrieveAccountsParams((prev) => {
      return {
        ...prev,
        page,
      };
    });
  };

  useEffect(() => {
    const loadOnboardingData = async () => {
      try {
        setIsLoading(true);

        const response = await axiosPrivate.get(
          `/web/merchants/${selectedEntity}`
        );
        const merchantData: RetrieveOneMerchantResponse = response.data;
        if (merchantData.onboarding) {
          const tabOnboardingData = retrieveOnboardingResponseToTab(
            merchantData.onboarding,
            merchantData.onboarding.merchantId
          );
          setOnboardingData(tabOnboardingData);
        }

        setMerchantData(merchantData);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };

    loadOnboardingData();
    loadAccountsData();
  }, []);

  return (
    <>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
        }}
      />
      <Title order={2} my="lg">
        View Merchant Information
      </Title>
      <EntityInfo
        isEntityActive={merchantData?.isActive}
        disabledReason={merchantData?.disabledReason}
        paginationRequest={paginationRequest}
        loadAccountsData={loadAccountsData}
        onboardingData={onboardingData}
        isLoading={isLoading}
        paginationData={paginationData}
        isAccountsLoading={isAccountsLoading}
        userData={userData}
      />
    </>
  );
}

export default MerchantInfo;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  selectedEntity: number;
}
